
import React from 'react';
import LoadSchedulingModal from './load-control/LoadSchedulingModal';
import LoadControlHeader from './load-control/LoadControlHeader';
import LoadCardGrid from './load-control/LoadCardGrid';
import LoadStatusTable from './load-control/LoadStatusTable';
import EfficiencyRecommendations from './load-control/EfficiencyRecommendations';
import LoadControlInfo from './load-control/LoadControlInfo';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LoadFloorMap from './load-control/LoadFloorMap';
import { Building, ListFilter, Map } from 'lucide-react';
import { floors } from './load-control/data/floorData';
import { useLoadControl } from './load-control/hooks/useLoadControl';

const LoadControlGrid: React.FC = () => {
  const {
    floorLoads,
    isSchedulingOpen,
    selectedLoad,
    activeRecommendation,
    selectedFloor,
    setSelectedFloor,
    selectedZone,
    setSelectedZone,
    viewMode,
    setViewMode,
    handleToggleStatus,
    openScheduling,
    handleSaveSchedule,
    toggleRecommendation,
    implementRecommendation,
    closeScheduling
  } = useLoadControl();

  return (
    <div className="grid grid-cols-1 gap-6 h-full p-4 overflow-y-auto max-h-[calc(100vh-150px)]">
      <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
        <div className="p-6">
          <LoadControlHeader />
          
          <div className="flex justify-between mb-6">
            <Tabs value={selectedFloor.toString()} onValueChange={(value) => setSelectedFloor(parseInt(value))}>
              <TabsList>
                {floors.map(floor => (
                  <TabsTrigger key={floor.id} value={floor.id.toString()} className="flex items-center">
                    <Building className="mr-1 h-4 w-4" />
                    {floor.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-transparent text-gray-500 dark:text-gray-400'}`}
              >
                <ListFilter className="h-5 w-5" />
              </button>
              <button 
                onClick={() => setViewMode('map')}
                className={`p-2 rounded-md ${viewMode === 'map' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-transparent text-gray-500 dark:text-gray-400'}`}
              >
                <Map className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {viewMode === 'list' ? (
            <>
              <LoadCardGrid 
                loads={floorLoads} 
                handleToggleStatus={handleToggleStatus} 
                openScheduling={openScheduling} 
              />
              
              <LoadStatusTable 
                loads={floorLoads} 
                handleToggleStatus={handleToggleStatus} 
                openScheduling={openScheduling} 
              />
            </>
          ) : (
            <LoadFloorMap 
              loads={floorLoads}
              selectedFloor={selectedFloor}
              selectedZone={selectedZone}
              onZoneSelect={setSelectedZone}
              handleToggleStatus={handleToggleStatus}
              openScheduling={openScheduling}
            />
          )}
          
          <EfficiencyRecommendations 
            loads={floorLoads} 
            activeRecommendation={activeRecommendation}
            toggleRecommendation={toggleRecommendation}
            implementRecommendation={implementRecommendation}
          />
          
          <LoadControlInfo />
        </div>
      </div>
      
      {isSchedulingOpen && selectedLoad && (
        <LoadSchedulingModal
          load={selectedLoad}
          onClose={closeScheduling}
          onSave={handleSaveSchedule}
        />
      )}
    </div>
  );
};

export default LoadControlGrid;
